/**
 * FleetMap — componente de mapa de frota COMPARTILHADO (fonte única).
 * ADR-2026-05-29-shared-fleetmap-component.
 *
 * PASSIVO: recebe todos os dados via props; NÃO faz fetch nem conversão de
 * unidade/SI (apresentação — INV-UI-TRAIL-001). Compõe: tiles padrão, marcadores
 * de equipamento (ícone canônico heading/COG), trilha(s), geofences e os
 * controllers de fit/follow/hover. Controles de UI (período de trajeto, seguir,
 * centralizar) entram via `children` (overlay), até serem padronizados.
 *
 * Diferenças entre páginas (ex.: kiosk mostra todos os marcadores mas foca um
 * equipamento) são props/flags, nunca forks.
 */

import React, { useMemo } from 'react';
import { MapContainer, Marker, Popup, Polyline, Polygon } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import FleetTileLayers from './FleetTileLayers';
import { FitBoundsController, FollowController, TrailHoverController } from './mapControllers';
import { createEquipmentIcon } from './equipmentIcon';

const TRAIL_BASE_COLOR = '#a855f7';

const createWaypointIcon = (color = '#38bdf8') => L.divIcon({
  className: 'fleetmap-waypoint',
  html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #020617;box-shadow:0 0 0 2px rgba(255,255,255,.35)"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const polygonPositions = (geofence) => {
  const ring = geofence?.polygon?.coordinates?.[0] || [];
  return ring.map(([lon, lat]) => [lat, lon]).filter(([lat, lon]) => lat != null && lon != null);
};

const FleetMap = ({
  equipments = [],
  trails = {},
  geofences = [],
  selectedEquipmentId = null,
  selectedFromSidebar = false,
  shouldFitBounds = false,
  onFitComplete,
  followedIds = null,
  onFollowDisengage = () => {},
  hoverEnabled = false,
  hoverPoints = [],
  onHoverChange = () => {},
  onPointClick = () => {},
  initialCenter = [-23.5505, -46.6333],
  initialZoom = 10,
  defaultLayer = 'satellite',
  onMarkerClick = () => {},
  renderPopup = null,
  mapRef = null,
  className = 'h-full w-full',
  children = null,
}) => {
  const eqsWithLoc = useMemo(
    () => equipments.filter(eq => eq.latitude != null && eq.longitude != null),
    [equipments],
  );
  const selectedEquipment = useMemo(
    () => equipments.find(eq => eq.id === selectedEquipmentId) || null,
    [equipments, selectedEquipmentId],
  );
  const followSet = followedIds || null;

  const trailPolylines = useMemo(() => {
    const out = [];
    Object.entries(trails || {}).forEach(([eqId, trail]) => {
      const positions = (trail?.points || [])
        .filter(p => p.lat != null && p.lon != null)
        .map(p => [p.lat, p.lon]);
      if (positions.length >= 2) out.push({ eqId, positions });
    });
    return out;
  }, [trails]);

  return (
    <div className={`relative ${className}`}>
      {children}
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        className="h-full w-full"
        style={{ background: '#020617' }}
        ref={mapRef || undefined}
        scrollWheelZoom
      >
        <FleetTileLayers defaultLayer={defaultLayer} />

        <FitBoundsController
          equipments={eqsWithLoc}
          selectedEquipment={selectedEquipment}
          shouldFitBounds={shouldFitBounds}
          selectedFromSidebar={selectedFromSidebar}
          onFitComplete={onFitComplete}
        />
        {followSet && (
          <FollowController
            equipments={eqsWithLoc}
            followedIds={followSet}
            onDisengage={onFollowDisengage}
          />
        )}
        <TrailHoverController
          enabled={hoverEnabled}
          points={hoverPoints}
          onHoverChange={onHoverChange}
          onPointClick={onPointClick}
        />

        {/* Geofences: polígonos + waypoints */}
        {geofences.map((gf) => {
          const geometryType = gf.geometry_type || (gf.point ? 'waypoint' : 'polygon');
          if (geometryType === 'waypoint' && Array.isArray(gf.point?.coordinates) && gf.point.coordinates.length >= 2) {
            const [lon, lat] = gf.point.coordinates;
            return (
              <Marker key={gf.id} position={[lat, lon]} icon={createWaypointIcon(gf.color)}>
                <Popup>{gf.name}</Popup>
              </Marker>
            );
          }
          const positions = polygonPositions(gf);
          if (!positions.length) return null;
          return (
            <Polygon
              key={gf.id}
              positions={positions}
              pathOptions={{ color: gf.color || '#f97316', weight: 2, fillOpacity: 0.12 }}
            />
          );
        })}

        {/* Trilha(s) */}
        {trailPolylines.map(({ eqId, positions }) => (
          <Polyline key={eqId} positions={positions} color={TRAIL_BASE_COLOR} weight={3} opacity={0.85} />
        ))}

        {/* Marcadores de equipamento (ícone canônico heading/COG) */}
        {eqsWithLoc.map((eq) => (
          <Marker
            key={eq.id}
            position={[eq.latitude, eq.longitude]}
            icon={createEquipmentIcon(
              eq.equipment_type_name,
              eq.marker_color,
              eq.heading,
              eq.cog,
              eq.speed_knots,
              !!(followSet && followSet.has(eq.id)),
            )}
            eventHandlers={{ click: () => onMarkerClick(eq) }}
          >
            {renderPopup && (
              <Popup maxWidth={460} minWidth={340} autoPanPadding={[24, 24]}>
                {renderPopup(eq)}
              </Popup>
            )}
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default FleetMap;
